import axios from 'axios';
import type { SignedApplicationAssetResponse } from '@/types/Shared';
import { baseUrl, HttpMethods } from '../../base';
import type {
  IDeleteResourceFilesService,
  IGetApplicationFormListServiceRes,
  IGetApplicationFormService,
  IGetApplicationService,
  IGetApplicationServiceResDataFile,
  IGetApplicationsService,
  IGetResourceFilesService,
  IGetResourceFilesServiceRes,
  IPatchApplicationFormService,
  IPatchApplicationService,
  IPatchArchiveApplicationService,
  IPostApplicationFormService,
  IPostApplicationService,
  IPostImportApplicationsService,
  IPostResourceFilesService,
  ISignApplicationPublicAssetPayload,
} from './types';

const applicationFormRootPath = `${baseUrl.ROVER}/application_forms`;
const applicationRootPath = `${baseUrl.ROVER}/applications`;

export async function postApplicationForm({ data }: IPostApplicationFormService) {
  const res = await axios({
    method: HttpMethods.POST,
    url: `${applicationFormRootPath}`,
    data,
  });
  return res.data;
}

export async function getApplicationForms({ params }: IGetApplicationFormService) {
  const res = await axios.get<IGetApplicationFormListServiceRes>(`${applicationFormRootPath}`, {
    params,
  });
  return res.data;
}

export async function getApplicationFormDetails({ applicationFormId }: { applicationFormId: string }) {
  const res = await axios({
    method: HttpMethods.GET,
    url: `${applicationFormRootPath}/${applicationFormId}`,
  });
  return res.data;
}

export async function getApplicationFormInfo({ applicationFormId }: { applicationFormId: string }) {
  const res = await axios({
    method: HttpMethods.GET,
    url: `${applicationFormRootPath}/${applicationFormId}/form_info`,
  });
  return res.data;
}

export async function patchApplicationForm({ applicationFormId, data }: IPatchApplicationFormService) {
  const res = await axios({
    method: HttpMethods.PATCH,
    url: `${applicationFormRootPath}/${applicationFormId}`,
    data,
  });
  return res.data;
}

export async function patchApplicationFormStatus({
  applicationFormId,
  action,
}: {
  applicationFormId: string;
  action: 'activate' | 'deactivate' | 'archive';
}) {
  const res = await axios({
    method: HttpMethods.PATCH,
    url: `${applicationFormRootPath}/${applicationFormId}/${action}`,
  });
  return res.data;
}

export async function archiveApplicationForm(applicationFormId: string) {
  const res = await axios({
    method: HttpMethods.PATCH,
    url: `${applicationFormRootPath}/${applicationFormId}/archive`,
  });
  return res.data;
}

export async function postImportApplications({ data, applicationFormId }: IPostImportApplicationsService) {
  const res = await axios({
    method: HttpMethods.POST,
    url: `${applicationFormRootPath}/${applicationFormId}/import_applications`,
    data,
  });
  return res.data;
}

export async function getApplications({ params }: IGetApplicationsService) {
  const res = await axios({
    method: HttpMethods.GET,
    url: `${applicationRootPath}`,
    params,
  });
  return res.data;
}

export async function postApplication({ data }: IPostApplicationService) {
  const res = await axios({
    method: HttpMethods.POST,
    url: `${applicationRootPath}`,
    data,
  });
  return res.data;
}

export async function getApplication({ applicationId }: IGetApplicationService) {
  const res = await axios({
    method: HttpMethods.GET,
    url: `${applicationRootPath}/${applicationId}`,
  });
  return res.data;
}

export async function patchApplication({ applicationId, data }: IPatchApplicationService) {
  const res = await axios({
    method: HttpMethods.PATCH,
    url: `${applicationRootPath}/${applicationId}`,
    data,
  });
  return res.data;
}

export async function patchArchiveApplication({ applicationId }: IPatchArchiveApplicationService) {
  const res = await axios({
    method: HttpMethods.PATCH,
    url: `${applicationRootPath}/${applicationId}/archive`,
  });
  return res.data;
}

// Resource Center Api
export async function postResourceFiles({ applicationFormId, data }: IPostResourceFilesService) {
  const res = await axios.request({
    method: HttpMethods.POST,
    url: `${baseUrl.ROVER}/application_forms/${applicationFormId}/assets/save`,
    data,
  });

  return res.data;
}

export async function getResourceFiles({ applicationFormId, params }: IGetResourceFilesService) {
  const res = await axios.request<IGetResourceFilesServiceRes[]>({
    method: HttpMethods.GET,
    url: `${baseUrl.ROVER}/application_forms/${applicationFormId}/assets`,
    params,
  });

  return res.data;
}

export async function deleteResourceFiles({ applicationFormId, assetsId }: IDeleteResourceFilesService) {
  const res = await axios.request({
    method: HttpMethods.DELETE,
    url: `${baseUrl.ROVER}/application_forms/${applicationFormId}/assets/${assetsId}`,
  });

  return res.data;
}

export const signApplicationPublicAsset = async ({ field, ...rest }: ISignApplicationPublicAssetPayload) => {
  const res = await axios.request<SignedApplicationAssetResponse>({
    url: `${baseUrl.SHERPA}/assets/application_public/sign`,
    method: 'POST',
    data: rest,
  });
  return { ...res.data, field };
};

export async function addApplicationFile({ applicationId, files }: { applicationId: string; files: IGetApplicationServiceResDataFile[] }) {
  const res = await axios({
    method: HttpMethods.PUT,
    url: `${applicationRootPath}/${applicationId}/file`,
    data: { files },
  });
  return res.data;
}

export async function deleteApplicationFile({ applicationId, key }: { applicationId: string; key: string }) {
  const res = await axios({
    method: HttpMethods.DELETE,
    url: `${applicationRootPath}/${applicationId}/file`,
    data: { key },
  });
  return res.data;
}
